import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import {
  Page,
  Card,
  Button,
  ButtonGroup,
  TextField,
  Select,
  Checkbox,
  TextContainer,
  Stack,
  Badge,
  Banner,
  Spinner,
  ProgressBar,
  Tooltip,
  Icon,
  Popover,
  ActionList,
  ResourceList,
  ResourceItem,
  Filters,
  ChoiceList,
  DataTable,
  EmptyState,
  Modal,
  TextStyle,
  Layout,
  SkeletonBodyText,
  SkeletonDisplayText,
  Tabs,
  Tag,
  Collapsible,
  Frame,
  Toast,
  FormLayout,
  Text
} from '@shopify/polaris';
import {
  SearchMajor,
  FilterMajor,
  SortMinor,
  ViewMajor,
  ListMajor,
  EditMajor,
  DeleteMajor,
  DuplicateMinor,
  CirclePlusMinor,
  CircleMinusMajor,
  ChevronDownMinor,
  ChevronUpMinor,
  LockMinor
} from '@shopify/polaris-icons';
import { auth, db } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import { useShop } from '../contexts/ShopContext';
import { useTemplateContext } from '../contexts/TemplateContext';
import ShopifyBulkOperationsService from '../services/shopifyBulkOperationsService';
import { deductCreditsAndRecordPush, hasEnoughCredits } from '../services/creditService';
import { useToast } from '../contexts/ToastContext';
import { createProductFieldHandlers } from '../handlers/productFieldHandlers';
import {
  handleBulkPriceUpdate,
  handleBulkInventoryUpdate,
  handleBulkCompareAtPriceUpdate,
  handleBulkSkuUpdate,
  handleBulkMetafieldUpdate,
  handleBulkOptionValueUpdate
} from '../handlers/bulkOperationHandlers';
import { createTemplateHandlers } from '../handlers/templateHandlers';
import { createUiHandlers } from '../handlers/uiHandlers';
import { createBulkOperationHandlers } from '../handlers/bulkOperationHandlers';
import { createApiHandlers } from '../handlers/apiHandlers';
import { handlePushToShopify as handleCreateNewProducts } from '../handlers/shopifyPushHandler';
import PricingTools from '../components/tools/PricingTools';
import InventoryTools from '../components/tools/InventoryTools';
import ProductTools from '../components/tools/ProductTools';
import SummernoteEditor from '../components/SummernoteEditor';
import TemplateTools from '../components/tools/TemplateTools';
import { formatPriceForDisplay, formatPriceForApi, getVariantFieldValue } from '../utils/productUtils';
import { SUBSCRIPTION_TYPES } from '../services/subscriptionService';

// Template service functions
const saveTemplate = async (templateData) => {
  console.log('Saving template to service:', templateData);
  // Here we would typically save to a database or API
  return true;
};

const loadTemplate = async (templateId) => {
  console.log('Loading template from service:', templateId);
  // Here we would typically load from a database or API
  return null;
};

const deleteTemplate = async (templateId) => {
  console.log('Deleting template from service:', templateId);
  // Here we would typically delete from a database or API
  return true;
};

const duplicateTemplate = async (templateId, newName) => {
  console.log('Duplicating template from service:', templateId, 'to', newName);
  // Here we would typically duplicate in a database or API
  return true;
};

export default function BulkEditProduct() {
  console.log('BulkEditProduct component rendering');
  
  // Initialize all state first
  const [searchParams] = useSearchParams();
  const { currentShop } = useShop();
  const { user } = useAuth();
  const location = useNavigate();

  // Create an instance of ShopifyBulkOperationsService
  const shopifyBulkOperationsService = useMemo(() => {
    if (currentShop) {
      return new ShopifyBulkOperationsService(currentShop.shop, currentShop.accessToken);
    }
    return null;
  }, [currentShop]);

  // State declarations
  const [products, setProducts] = useState([]);
  const [selectedProductIds, setSelectedProductIds] = useState(new Set());
  const [expandedProducts, setExpandedProducts] = useState(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [savingProgress, setSavingProgress] = useState({ current: 0, total: 0, message: '' });
  const [error, setError] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showCreateNewProductsModal, setShowCreateNewProductsModal] = useState(false);
  const [showCreationConfirmModal, setShowCreationConfirmModal] = useState(false);
  const [deleteOldProducts, setDeleteOldProducts] = useState(false);
  const [isPriceUpdating, setIsPriceUpdating] = useState(false);
  const [isInventoryUpdating, setIsInventoryUpdating] = useState(false);
  const [isProductUpdating, setIsProductUpdating] = useState(false);
  const [localEdits, setLocalEdits] = useState({});
  const [loadingProgress, setLoadingProgress] = useState({ current: 0, total: 0 });
  const [viewMode, setViewMode] = useState('card'); // 'card' or 'list'
  const [columnVisibility, setColumnVisibility] = useState([
    true,  // Checkbox
    true,  // Image
    true,  // Title
    true,  // Handle
    true,  // Description
    true,  // Product Type
    true,  // Vendor
    true,  // Tags
    true,  // Featured Image Alt Text
    true,  // Option1 Name
    true,  // Option1 Values
    true,  // Option2 Name
    true,  // Option2 Values
    true,  // Option3 Name
    true,  // Option3 Values
    true,  // Variant Options
    true,  // Variant SKU
    true,  // Variant Price
    true,  // Variant Compare at Price
    true,  // Variant Inventory
    true   // Metafields
  ]);
  
  const columnNames = [
    'Checkbox',
    'Image',
    'Title',
    'Handle',
    'Description',
    'Product Type',
    'Vendor',
    'Tags',
    'Product Image Alt Text',
    'Option1 Name',
    'Option1 Values',
    'Option2 Name',
    'Option2 Values',
    'Option3 Name',
    'Option3 Values',
    'Variant Options',
    'Variant SKU',
    'Variant Price',
    'Variant Compare at Price',
    'Variant Inventory',
    'Metafields'
  ];

  const [columnWidths, setColumnWidths] = useState({
    'Checkbox': 50,
    'Image': 100,
    'Title': 200,
    'Handle': 150,
    'Description': 400,
    'Product Type': 150,
    'Vendor': 150,
    'Tags': 200,
    'Product Image Alt Text': 200,
    'Option1 Name': 150,
    'Option1 Values': 150,
    'Option2 Name': 150,
    'Option2 Values': 150,
    'Option3 Name': 150,
    'Option3 Values': 150,
    'Variant Options': 300,
    'Variant SKU': 150,
    'Variant Price': 150,
    'Variant Compare at Price': 150,
    'Variant Inventory': 150,
    'Metafields': 300
  });
  const [templateProducts, setTemplateProducts] = useState([]);
  const [priceChange, setPriceChange] = useState('');
  const [priceChangeType, setPriceChangeType] = useState('increase');
  const [roundTo, setRoundTo] = useState('');
  const [roundingType, setRoundingType] = useState('up');
  const [priceTools, setPriceTools] = useState({
    minimumPrice: '10',
    fixedAmount: '10',
    margin: '60',
    salesPriceFactor: '2.3',
    roundingValue: '0.95',
    compareFactor: '50'
  });
  const [priceToolsVisible, setPriceToolsVisible] = useState(false);
  const [inventoryToolsVisible, setInventoryToolsVisible] = useState(false);
  const [productToolsVisible, setProductToolsVisible] = useState(false);
  const [productHandleProductChange, setProductHandleProductChange] = useState({});

  // Initialize toast context
  const toastContext = useToast();
  const showToast = toastContext ? toastContext.showToast : (message) => console.log('Toast:', message);

  // Function to show toast using local state
  const showLocalToast = useCallback((content, error = false, duration = error ? null : 3000) => {
    showToast(content, error, duration);
  }, [showToast]);

  // Get template context functions
  const {
    templates,
    createTemplate,
    updateTemplate,
    deleteTemplate: deleteTemplateContext,
    executeTemplate,
    executeTemplates,
    registerHandleProductChange,
    registerHandleOptionValueChange,
    registerHandleVariantChange,
    productEdits: templateProductEdits,
    setSelectedProducts: setTemplateSelectedProducts,
    selectedProducts: templateSelectedProducts,
  } = useTemplateContext();

  // Initialize product field handlers
  const {
    handleProductChange,
    handleVariantChange,
    handleMultipleVariantChanges,
    handleMetafieldChange,
    handleOptionChange,
    handleOptionValueChange,
    handleOptionNameChange,
    createProductFieldChangeHandler,
    createVariantFieldChangeHandler,
    createMetafieldChangeHandler
  } = createProductFieldHandlers(
    setLocalEdits,
    setHasUnsavedChanges,
    showToast
  );

  // Register handlers with the template context
  useEffect(() => {
    if (registerHandleProductChange) {
      registerHandleProductChange(handleProductChange);
    }
    if (registerHandleOptionValueChange) {
      registerHandleOptionValueChange(handleOptionValueChange);
    }
    if (registerHandleVariantChange) {
      // Register the enhanced variant change handler that can handle both single and multiple variant updates
      registerHandleVariantChange((productId, variantIdOrUpdates, field, value) => {
        // Check if this is a multiple variant update (second parameter is an object of variant updates)
        if (typeof variantIdOrUpdates === 'object' && !field && !value) {
          handleMultipleVariantChanges(productId, variantIdOrUpdates);
        } else {
          // Standard single variant update
          handleVariantChange(productId, variantIdOrUpdates, field, value);
        }
      });
    }
  }, [registerHandleProductChange, registerHandleOptionValueChange, registerHandleVariantChange, 
      handleProductChange, handleOptionValueChange, handleVariantChange, handleMultipleVariantChanges]);

  const {
    handleBulkUpdateMetafields,
    handlePriceUpdate,
    handleBulkPriceChange,
    handleRoundPrices,
    handleInventoryUpdate,
    handleProductUpdate
  } = createBulkOperationHandlers(
    products,
    setProducts,
    localEdits,
    setLocalEdits,
    selectedProductIds,
    setHasUnsavedChanges,
    showLocalToast,
    null,
    setIsInventoryUpdating,
    setIsProductUpdating
  );

  const {
    handleSaveTemplate,
    handleLoadTemplate,
    handleDeleteTemplate,
    handleDuplicateTemplate,
    handleTemplateUpdate
  } = createTemplateHandlers(
    setLocalEdits,
    setHasUnsavedChanges,
    showToast,
    saveTemplate,
    loadTemplate,
    deleteTemplate,
    duplicateTemplate,
    localEdits
  );

  const {
    handleSelectAll,
    handleProductSelect,
    handleExpandToggle,
    toggleColumnVisibility,
    toggleAllColumnsVisibility,
    handleViewModeChange,
    handleResizeStart
  } = createUiHandlers(
    products,
    selectedProductIds,
    setSelectedProductIds,
    expandedProducts,
    setExpandedProducts,
    columnVisibility,
    setColumnVisibility,
    setViewMode,
    columnWidths,
    setColumnWidths
  );

  // Memoize the API handlers to prevent unnecessary re-creation
  const apiHandlers = useMemo(() => createApiHandlers(
    currentShop,
    user,
    shopifyBulkOperationsService
  ), [currentShop, user, shopifyBulkOperationsService]);

  const {
    handleApiRequest,
    handleApiRequestWithFile,
    handleFetchProducts,
    handlePushToShopify,
    handleSave
  } = apiHandlers;

  // Make localEdits available globally for template system
  useEffect(() => {
    // Initialize localEdits with original metafield values if they don't exist yet
    const initializedLocalEdits = { ...localEdits };
    let madeChanges = false;
    
    // For each product, ensure metafields are properly initialized in localEdits
    products.forEach(product => {
      const productId = product.id;
      
      // Skip if this product doesn't have metafields
      if (!product.metafields || product.metafields.length === 0) {
        return;
      }
      
      // Initialize product entry in localEdits if it doesn't exist
      if (!initializedLocalEdits[productId]) {
        initializedLocalEdits[productId] = { _timestamp: Date.now() };
        madeChanges = true;
      }
      
      // Initialize metafields object if it doesn't exist
      if (!initializedLocalEdits[productId].metafields) {
        initializedLocalEdits[productId].metafields = {};
        madeChanges = true;
      }
      
      // Initialize metafield_updates if it doesn't exist
      if (!initializedLocalEdits[productId].metafield_updates) {
        initializedLocalEdits[productId].metafield_updates = {};
        madeChanges = true;
      }
      
      // Initialize metafields_array if it doesn't exist
      if (!initializedLocalEdits[productId].metafields_array) {
        initializedLocalEdits[productId].metafields_array = [];
        madeChanges = true;
      }
      
      // Add all metafields from the product to localEdits if they don't exist yet
      product.metafields.forEach(metafield => {
        if (metafield && metafield.namespace && metafield.key) {
          const metafieldKey = `${metafield.namespace}.${metafield.key}`;
          
          // Only add if this metafield doesn't already exist in localEdits
          if (!initializedLocalEdits[productId].metafields[metafieldKey]) {
            // Add to metafields object
            initializedLocalEdits[productId].metafields[metafieldKey] = {
              value: metafield.value,
              _timestamp: Date.now()
            };
            
            // Add to metafield_updates
            initializedLocalEdits[productId].metafield_updates[metafieldKey] = metafield.value;
            
            // Check if it exists in metafields_array
            const existingIndex = initializedLocalEdits[productId].metafields_array.findIndex(
              m => m.namespace === metafield.namespace && m.key === metafield.key
            );
            
            // Add to metafields_array if it doesn't exist
            if (existingIndex < 0) {
              initializedLocalEdits[productId].metafields_array.push({
                namespace: metafield.namespace,
                key: metafield.key,
                value: metafield.value,
                type: metafield.type || 'single_line_text_field'
              });
            }
            
            madeChanges = true;
            console.log(`Initialized metafield ${metafieldKey} with value:`, metafield.value);
          }
        }
      });
    });
    
    // Update localEdits if we made changes
    if (madeChanges) {
      console.log('Initialized localEdits with original metafield values:', initializedLocalEdits);
      setLocalEdits(initializedLocalEdits);
    } else {
      // Still make window.localEdits available
      window.localEdits = localEdits;
    }
    
    return () => {
      delete window.localEdits;
    };
  }, [localEdits, products]);

  // Create a ref to track if we've already fetched the products
  const fetchedIdsRef = useRef(null);

  useEffect(() => {
    const productIds = searchParams.get('ids');
    if (!currentShop || !productIds) {
      console.log('No shop or product IDs, skipping fetch');
      return;
    }

    // Check if we've already fetched these exact same product IDs
    const currentIdsString = productIds.split(',').sort().join(',');
    if (fetchedIdsRef.current === currentIdsString) {
      console.log('Already fetched these product IDs, skipping duplicate fetch');
      return;
    }

    console.log('Fetching products with IDs:', currentIdsString);
    handleFetchProducts(
      currentShop,
      productIds.split(','),
      setIsLoading,
      setError,
      setProducts,
      setLoadingProgress
    );
    
    // Update the ref with the current IDs we just fetched
    fetchedIdsRef.current = currentIdsString;
  }, [currentShop, searchParams, handleFetchProducts]);

  useEffect(() => {
    if (registerHandleProductChange) {
      registerHandleProductChange(handleProductChange);
    }
  }, [handleProductChange, registerHandleProductChange]);

  useEffect(() => {
    if (registerHandleOptionValueChange) {
      registerHandleOptionValueChange(handleOptionValueChange);
    }
  }, [handleOptionValueChange, registerHandleOptionValueChange]);

  useEffect(() => {
    if (currentShop?.shop && currentShop?.accessToken) {
      shopifyBulkOperationsService.setShopCredentials(currentShop.shop, currentShop.accessToken);
    }
  }, [currentShop, shopifyBulkOperationsService]);

  useEffect(() => {
    console.log('Syncing products with TemplateContext:', products.length);
    setTemplateProducts(products);
  }, [products, setTemplateProducts]);

  useEffect(() => {
    if (products && products.length > 0) {
      console.log('Raw product data:', {
        id: products[0].id,
        title: products[0].title,
        vendor: products[0].vendor,
        productType: products[0].productType,
        allKeys: Object.keys(products[0])
      });
      
      // Log the vendor field for all products
      products.forEach((product, index) => {
        console.log(`Product ${index} vendor:`, product.vendor);
      });
    }
  }, [products]);

  // State to store user document from Firestore
  const [userDoc, setUserDoc] = useState(null);

  useEffect(() => {
    const fetchUserDocument = async () => {
      if (!user) return;
      
      try {
        const userRef = doc(db, 'users', user.uid);
        const userSnapshot = await getDoc(userRef);
        
        if (userSnapshot.exists()) {
          setUserDoc(userSnapshot.data());
          console.log('User document fetched:', userSnapshot.data());
        }
      } catch (error) {
        console.error('Error fetching user document:', error);
      }
    };
    
    fetchUserDocument();
  }, [user]);

  // Function to render metafields in card view
  const renderMetafields = useCallback((product) => {
    if (!product.metafields || product.metafields.length === 0) return null;

    return (
      <Card title="Metafields">
        <Card.Section>
          <FormLayout>
            {product.metafields.map((metafield, index) => {
              const metafieldKey = `${metafield.namespace}.${metafield.key}`;
              // Check both metafield_updates and metafields in localEdits
              const currentValue = 
                localEdits[product.id]?.metafield_updates?.[metafieldKey] || 
                localEdits[product.id]?.metafields?.[metafieldKey]?.value || 
                metafield.value;
              
              return (
                <FormLayout.Group key={metafield.id || `new-${index}`}>
                  <TextField
                    label={`${metafield.namespace}.${metafield.key}`}
                    value={(() => {
                      const metafieldKey = `${metafield.namespace}.${metafield.key}`;
                      // Check both metafield_updates and metafields in localEdits
                      return localEdits[product.id]?.metafield_updates?.[metafieldKey] || 
                             localEdits[product.id]?.metafields?.[metafieldKey]?.value || 
                             metafield.value;
                    })()}
                    {...createMetafieldChangeHandler(product.id, metafield.namespace, metafield.key)}
                    autoComplete="off"
                    type="text"
                    id={`metafield-${metafield.namespace}-${metafield.key}-${product.id}`}
                  />
                </FormLayout.Group>
              );
            })}
          </FormLayout>
        </Card.Section>
      </Card>
    );
  }, [localEdits, createMetafieldChangeHandler]);

  const renderProductCard = useCallback((product) => {
    if (!product) return null;
    
    const productEdits = localEdits[product.id] || {};
    
    return (
      <Card>
        <div style={{ padding: '16px' }}>
          <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
            <div style={{ flex: 1 }}>
              <FormLayout>
                <FormLayout.Group>
                  <TextField
                    label="Title"
                    value={localEdits[product.id]?.title ?? product.title}
                    {...createProductFieldChangeHandler(product.id, 'title')}
                    autoComplete="off"
                    type="text"
                    id={`title-${product.id}`}
                  />
                </FormLayout.Group>
                <FormLayout.Group>
                  <TextField
                    label="Handle"
                    value={localEdits[product.id]?.handle ?? product.handle}
                    {...createProductFieldChangeHandler(product.id, 'handle')}
                    autoComplete="off"
                    type="text"
                    id={`handle-${product.id}`}
                  />
                </FormLayout.Group>
                <FormLayout.Group>
                  <TextField
                    label="Product Type"
                    value={productEdits.product_type ?? product.productType}
                    {...createProductFieldChangeHandler(product.id, 'product_type')}
                    autoComplete="off"
                    type="text"
                    id={`product-type-${product.id}`}
                  />
                  <TextField
                    label="Tags"
                    value={localEdits[product.id]?.tags ?? product.tags}
                    {...createProductFieldChangeHandler(product.id, 'tags')}
                    autoComplete="off"
                    type="text"
                    id={`tags-${product.id}`}
                  />
                  <TextField
                    label="Vendor"
                    value={(localEdits[product.id]?.vendor ?? product.vendor) || ''}
                    {...createProductFieldChangeHandler(product.id, 'vendor')}
                    autoComplete="off"
                    type="text"
                    id={`vendor-${product.id}`}
                  />
                </FormLayout.Group>
                <FormLayout.Group>
                  <TextField
                    label="Product Image Alt Text"
                    value={(localEdits[product.id]?.image_alt_text ?? (product.featuredImage?.altText || (product.images && product.images[0]?.altText))) || ''}
                    {...createProductFieldChangeHandler(product.id, 'image_alt_text')}
                    autoComplete="off"
                    type="text"
                    id={`image-alt-text-${product.id}`}
                  />
                </FormLayout.Group>
              </FormLayout>
            </div>
          </div>
          
          {/* Description */}
          <Card title="Description" sectioned>
            <SummernoteEditor
              value={productEdits.descriptionHtml ?? product.descriptionHtml}
              onChange={(value) => handleProductChange(product.id, 'descriptionHtml', value)}
              id={`description-${product.id}`}
              onFocus={(e) => { if (e && e.stopPropagation) e.stopPropagation(); }}
              onClick={(e) => { if (e && e.stopPropagation) e.stopPropagation(); }}
            />
          </Card>
          
          {/* Options */}
          <Card title="Options">
            <Card.Section>
              <FormLayout>
                <FormLayout.Group>
                  <TextField
                    label="Option 1 Name"
                    value={(localEdits[product.id]?.option1_name ?? product.options?.[0]?.name) || ''}
                    onChange={(value) => {
                      setLocalEdits(prev => {
                        const productEdits = prev[product.id] || {};
                        
                        // Create a new object with the updated option name
                        const updatedEdits = {
                          ...prev,
                          [product.id]: {
                            ...productEdits,
                            option1_name: value
                          }
                        };
                        
                        // Force a re-render to update variant titles
                        return updatedEdits;
                      });
                    }}
                    onClearButtonClick={(e) => {
                      if (e && e.stopPropagation) e.stopPropagation();
                      setLocalEdits(prev => {
                        const productEdits = prev[product.id] || {};
                        
                        // Create a new object with the updated option name
                        const updatedEdits = {
                          ...prev,
                          [product.id]: {
                            ...productEdits,
                            option1_name: ''
                          }
                        };
                        
                        // Force a re-render to update variant titles
                        return updatedEdits;
                      });
                    }}
                    onFocus={(e) => { if (e && e.stopPropagation) e.stopPropagation(); }}
                    onClick={(e) => { if (e && e.stopPropagation) e.stopPropagation(); }}
                    autoComplete="off"
                    type="text"
                    id={`option1-name-${product.id}`}
                  />
                  {product.options && product.options.length > 1 && (
                    <TextField
                      label="Option 2 Name"
                      value={(localEdits[product.id]?.option2_name ?? product.options?.[1]?.name) || ''}
                      onChange={(value) => {
                        setLocalEdits(prev => {
                          const productEdits = prev[product.id] || {};
                          
                          // Create a new object with the updated option name
                          const updatedEdits = {
                            ...prev,
                            [product.id]: {
                              ...productEdits,
                              option2_name: value
                            }
                          };
                          
                          // Force a re-render to update variant titles
                          return updatedEdits;
                        });
                      }}
                      onClearButtonClick={(e) => {
                        if (e && e.stopPropagation) e.stopPropagation();
                        setLocalEdits(prev => {
                          const productEdits = prev[product.id] || {};
                          
                          // Create a new object with the updated option name
                          const updatedEdits = {
                            ...prev,
                            [product.id]: {
                              ...productEdits,
                              option2_name: ''
                            }
                          };
                          
                          // Force a re-render to update variant titles
                          return updatedEdits;
                        });
                      }}
                      onFocus={(e) => { if (e && e.stopPropagation) e.stopPropagation(); }}
                      onClick={(e) => { if (e && e.stopPropagation) e.stopPropagation(); }}
                      autoComplete="off"
                      type="text"
                      id={`option2-name-${product.id}`}
                    />
                  )}
                  {product.options && product.options.length > 2 && (
                    <TextField
                      label="Option 3 Name"
                      value={(localEdits[product.id]?.option3_name ?? product.options?.[2]?.name) || ''}
                      onChange={(value) => {
                        setLocalEdits(prev => {
                          const productEdits = prev[product.id] || {};
                          
                          // Create a new object with the updated option name
                          const updatedEdits = {
                            ...prev,
                            [product.id]: {
                              ...productEdits,
                              option3_name: value
                            }
                          };
                          
                          // Force a re-render to update variant titles
                          return updatedEdits;
                        });
                      }}
                      onClearButtonClick={(e) => {
                        if (e && e.stopPropagation) e.stopPropagation();
                        setLocalEdits(prev => {
                          const productEdits = prev[product.id] || {};
                          
                          // Create a new object with the updated option name
                          const updatedEdits = {
                            ...prev,
                            [product.id]: {
                              ...productEdits,
                              option3_name: ''
                            }
                          };
                          
                          // Force a re-render to update variant titles
                          return updatedEdits;
                        });
                      }}
                      onFocus={(e) => { if (e && e.stopPropagation) e.stopPropagation(); }}
                      onClick={(e) => { if (e && e.stopPropagation) e.stopPropagation(); }}
                      autoComplete="off"
                      type="text"
                      id={`option3-name-${product.id}`}
                    />
                  )}
                </FormLayout.Group>
              </FormLayout>
            </Card.Section>
            <Card.Section title="Option Values">
              <FormLayout>
                <FormLayout.Group>
                  <div>
                    <Text variant="bodyMd" as="p" fontWeight="medium">Option 1 Values</Text>
                    <Stack vertical spacing="tight">
                      {(() => {
                        const editedOptions = localEdits[product.id]?.options || product.options;
                        const option1 = editedOptions?.find(o => o.position === 1) || product.options?.find(o => o.position === 1);
                        
                        if (!option1) return null;
                        
                        return option1.values.map((value, valueIndex) => {
                          const editedValue = localEdits[product.id]?.options?.[0]?.values?.[valueIndex] ?? value;
                          
                          return (
                            <TextField
                              key={`option1-value-${valueIndex}`}
                              label={`Value ${valueIndex + 1}`}
                              labelHidden
                              value={editedValue}
                              onChange={(newValue, e) => {
                                console.log(`Option 1 value ${valueIndex} change:`, newValue);
                                if (e && e.stopPropagation) e.stopPropagation();
                                handleOptionValueChange(product.id, 0, newValue, valueIndex);
                              }}
                              autoComplete="off"
                              clearButton
                              onClearButtonClick={(e) => {
                                if (e && e.stopPropagation) e.stopPropagation();
                                handleOptionValueChange(product.id, 0, '', valueIndex);
                              }}
                              onFocus={(e) => { if (e && e.stopPropagation) e.stopPropagation(); }}
                              onClick={(e) => { if (e && e.stopPropagation) e.stopPropagation(); }}
                            />
                          );
                        });
                      })()}
                    </Stack>
                  </div>
                  {product.options && product.options.length > 1 && (
                    <div>
                      <Text variant="bodyMd" as="p" fontWeight="medium">Option 2 Values</Text>
                      <Stack vertical spacing="tight">
                        {(() => {
                          const editedOptions = localEdits[product.id]?.options || product.options;
                          const option2 = editedOptions?.find(o => o.position === 2) || product.options?.find(o => o.position === 2);
                          
                          if (!option2) return null;
                          
                          return option2.values.map((value, valueIndex) => {
                            const editedValue = localEdits[product.id]?.options?.[1]?.values?.[valueIndex] ?? value;
                            
                            return (
                              <TextField
                                key={`option2-value-${valueIndex}`}
                                label={`Value ${valueIndex + 1}`}
                                labelHidden
                                value={editedValue}
                                onChange={(newValue, e) => {
                                  console.log(`Option 2 value ${valueIndex} change:`, newValue);
                                  if (e && e.stopPropagation) e.stopPropagation();
                                  handleOptionValueChange(product.id, 1, newValue, valueIndex);
                                }}
                                autoComplete="off"
                                clearButton
                                onClearButtonClick={(e) => {
                                  if (e && e.stopPropagation) e.stopPropagation();
                                  handleOptionValueChange(product.id, 1, '', valueIndex);
                                }}
                                onFocus={(e) => { if (e && e.stopPropagation) e.stopPropagation(); }}
                                onClick={(e) => { if (e && e.stopPropagation) e.stopPropagation(); }}
                              />
                            );
                          });
                        })()}
                      </Stack>
                    </div>
                  )}
                  {product.options && product.options.length > 2 && (
                    <div>
                      <Text variant="bodyMd" as="p" fontWeight="medium">Option 3 Values</Text>
                      <Stack vertical spacing="tight">
                        {(() => {
                          const editedOptions = localEdits[product.id]?.options || product.options;
                          const option3 = editedOptions?.find(o => o.position === 3) || product.options?.find(o => o.position === 3);
                          
                          if (!option3) return null;
                          
                          return option3.values.map((value, valueIndex) => {
                            const editedValue = localEdits[product.id]?.options?.[2]?.values?.[valueIndex] ?? value;
                            
                            return (
                              <TextField
                                key={`option3-value-${valueIndex}`}
                                label={`Value ${valueIndex + 1}`}
                                labelHidden
                                value={editedValue}
                                onChange={(newValue, e) => {
                                  console.log(`Option 3 value ${valueIndex} change:`, newValue);
                                  if (e && e.stopPropagation) e.stopPropagation();
                                  handleOptionValueChange(product.id, 2, newValue, valueIndex);
                                }}
                                autoComplete="off"
                                clearButton
                                onClearButtonClick={(e) => {
                                  if (e && e.stopPropagation) e.stopPropagation();
                                  handleOptionValueChange(product.id, 2, '', valueIndex);
                                }}
                                onFocus={(e) => { if (e && e.stopPropagation) e.stopPropagation(); }}
                                onClick={(e) => { if (e && e.stopPropagation) e.stopPropagation(); }}
                              />
                            );
                          });
                        })()}
                      </Stack>
                    </div>
                  )}
                </FormLayout.Group>
              </FormLayout>
            </Card.Section>
          </Card>
          
          {/* Metafields */}
          {renderMetafields(product)}
          
          {/* Variants */}
          <Card title="Variants">
            <Card.Section>
              {product.variants && product.variants.map((variant, index) => {
                const variantTitle = getVariantTitle(product, variant);
                
                return (
                  <div key={variant.id}>
                    <div style={{ 
                      marginBottom: '8px', 
                      fontWeight: 'bold',
                      fontSize: '14px',
                      color: '#202223',
                      padding: '8px',
                      backgroundColor: '#f6f6f7',
                      borderRadius: '4px',
                      textAlign: 'center',
                      border: '1px solid #dfe3e8'
                    }}>
                      {variantTitle || `Variant ${index + 1}`}
                    </div>
                    <div style={{ 
                      padding: '12px', 
                      border: '1px solid var(--p-border-subdued)', 
                      borderRadius: '4px',
                      marginBottom: '16px'
                    }}>
                      <FormLayout>
                        <FormLayout.Group>
                          <div>
                            <Text variant="bodyMd" as="p" fontWeight="bold">
                              SKU
                            </Text>
                            <TextField
                              value={getVariantFieldValue(product.id, variant.id, 'sku', variant.sku, localEdits)}
                              {...createVariantFieldChangeHandler(product.id, variant.id, 'sku')}
                              autoComplete="off"
                              type="text"
                              id={`sku-${variant.id}`}
                            />
                          </div>
                          <div>
                            <Text variant="bodyMd" as="p" fontWeight="bold">
                              Price
                            </Text>
                            <TextField
                              value={formatPriceForDisplay(getVariantFieldValue(product.id, variant.id, 'price', variant.price, localEdits))}
                              {...createVariantFieldChangeHandler(product.id, variant.id, 'price', formatPriceForApi)}
                              prefix="$"
                              autoComplete="off"
                              type="text"
                              id={`price-${variant.id}`}
                            />
                          </div>
                          <div>
                            <Text variant="bodyMd" as="p" fontWeight="bold">
                              Compare At Price
                            </Text>
                            <TextField
                              value={formatPriceForDisplay(getVariantFieldValue(product.id, variant.id, 'compare_at_price', variant.compareAtPrice, localEdits))}
                              {...createVariantFieldChangeHandler(product.id, variant.id, 'compare_at_price', formatPriceForApi)}
                              prefix="$"
                              autoComplete="off"
                              type="text"
                              id={`compare-at-price-${variant.id}`}
                            />
                          </div>
                          <div>
                            <Text variant="bodyMd" as="p" fontWeight="bold">
                              Inventory
                            </Text>
                            <TextField
                              value={getVariantFieldValue(product.id, variant.id, 'inventory_quantity', variant.inventoryQuantity, localEdits)}
                              {...createVariantFieldChangeHandler(product.id, variant.id, 'inventory_quantity')}
                              autoComplete="off"
                              type="text"
                              id={`inventory-${variant.id}`}
                            />
                          </div>
                        </FormLayout.Group>
                      </FormLayout>
                    </div>
                  </div>
                );
              })}
            </Card.Section>
          </Card>
        </div>
      </Card>
    );
  }, [localEdits, handleProductChange, handleVariantChange, getVariantFieldValue, formatPriceForDisplay, formatPriceForApi, renderMetafields, handleOptionNameChange, handleOptionValueChange]);

  // Component to render a variant card with title
  const VariantCard = ({ product, variant, children }) => {
    return (
      <div style={{ marginBottom: '16px' }}>
        <div style={{ 
          marginBottom: '8px', 
          fontWeight: 'bold',
          fontSize: '14px',
          color: '#202223',
          padding: '8px',
          backgroundColor: '#f6f6f7',
          borderRadius: '4px',
          textAlign: 'center'
        }}>
          {getVariantTitle(product, variant)}
        </div>
        <div style={{ 
          padding: '12px', 
          border: '1px solid var(--p-border-subdued)', 
          borderRadius: '4px' 
        }}>
          <FormLayout>
            <FormLayout.Group>
              {children}
            </FormLayout.Group>
          </FormLayout>
        </div>
      </div>
    );
  };

  const getChangedFields = useCallback((productId) => {
    return localEdits[productId] || {};
  }, [localEdits]);

  // Generate a title for a variant that combines option names and values
  const getVariantTitle = useCallback((product, variant) => {
    if (!product || !variant) return 'Variant';
    
    // Try to get option values from different sources
    let option1, option2, option3;
    
    // First try to get from selectedOptions array (Shopify API format)
    if (variant.selectedOptions && Array.isArray(variant.selectedOptions)) {
      option1 = variant.selectedOptions[0]?.value;
      option2 = variant.selectedOptions[1]?.value;
      option3 = variant.selectedOptions[2]?.value;
    }
    
    // If not found, try direct option properties
    if (!option1 && variant.option1) option1 = variant.option1;
    if (!option2 && variant.option2) option2 = variant.option2;
    if (!option3 && variant.option3) option3 = variant.option3;
    
    const parts = [];
    
    // Get option names from product (using local edits if available)
    const option1Name = (localEdits[product.id]?.option1_name ?? product.options?.[0]?.name) || "Option 1";
    const option2Name = (localEdits[product.id]?.option2_name ?? product.options?.[1]?.name) || "Option 2";
    const option3Name = (localEdits[product.id]?.option3_name ?? product.options?.[2]?.name) || "Option 3";
    
    // Get option values from variant (using local edits if available)
    const option1Value = getVariantFieldValue(product.id, variant.id, 'option1', option1, localEdits);
    const option2Value = getVariantFieldValue(product.id, variant.id, 'option2', option2, localEdits);
    const option3Value = getVariantFieldValue(product.id, variant.id, 'option3', option3, localEdits);
    
    // Add option1 name and value if they exist
    if (option1Value) {
      parts.push(`${option1Value}`);
    }
    
    // Add option2 name and value if they exist
    if (option2Value) {
      parts.push(`${option2Value}`);
    }
    
    // Add option3 name and value if they exist
    if (option3Value) {
      parts.push(`${option3Value}`);
    }
    
    // If no parts were added, use a default title
    if (parts.length === 0) {
      return variant.title || 'Variant';
    }
    
    return parts.join(' / ');
  }, [localEdits]);

  const getVariantOptions = useCallback((product, variant) => {
    if (!product || !variant) return '';
    
    const options = [];
    
    if (variant.option1) {
      const option1Name = (localEdits[product.id]?.option1_name ?? product.options?.[0]?.name) || "Option 1";
      const option1Value = getVariantFieldValue(product.id, variant.id, 'option1', variant.option1, localEdits);
      options.push(`${option1Name}: ${option1Value}`);
    }
    
    if (variant.option2) {
      const option2Name = (localEdits[product.id]?.option2_name ?? product.options?.[1]?.name) || "Option 2";
      const option2Value = getVariantFieldValue(product.id, variant.id, 'option2', variant.option2, localEdits);
      options.push(`${option2Name}: ${option2Value}`);
    }
    
    if (variant.option3) {
      const option3Name = (localEdits[product.id]?.option3_name ?? product.options?.[2]?.name) || "Option 3";
      const option3Value = getVariantFieldValue(product.id, variant.id, 'option3', variant.option3, localEdits);
      options.push(`${option3Name}: ${option3Value}`);
    }
    
    return options.join(' / ');
  }, [localEdits, getVariantFieldValue]);

  const isVariantFieldChanged = useCallback((productId, variantId, fieldName, originalValue) => {
    if (!localEdits[productId] || !localEdits[productId].variants || !localEdits[productId].variants[variantId]) {
      return false;
    }
    
    const editedValue = localEdits[productId].variants[variantId][fieldName];
    return editedValue !== undefined && editedValue !== originalValue;
  }, [localEdits]);

  const createOptionNameChangeHandler = useCallback((productId, optionIndex) => {
    return {
      onChange: (value) => {
        setLocalEdits(prev => {
          const productEdits = prev[productId] || {};
          
          // Create a new object with the updated option name
          const updatedEdits = {
            ...prev,
            [productId]: {
              ...productEdits,
              [`option${optionIndex + 1}_name`]: value
            }
          };
          
          // Force a re-render to update variant titles
          return updatedEdits;
        });
      }
    };
  }, []);

  // Function to check if user has access to specific tools based on subscription plan
  const getUserPlanLevel = useCallback(() => {
    console.log('Checking user plan level with userDoc:', userDoc);
    
    let planLevel = 1; // Default to level 1
    
    if (!userDoc) {
      console.log('No user document found, using default plan level');
      return planLevel;
    }
    
    // First check if user is VIP
    if (userDoc.isVIP) {
      console.log('User is VIP');
      
      // For VIP users, use their assigned VIP plan level
      const vipPlanType = userDoc.vipPlan || '';
      console.log('VIP plan type:', vipPlanType);
      
      if (vipPlanType) {
        // Extract the plan level number (e.g., 'plan_5' -> 5)
        const vipLevel = parseInt(vipPlanType.split('_')[1]) || 1; // Default to 1 if parsing fails
        console.log('VIP plan level:', vipLevel);
        planLevel = vipLevel;
      } else {
        // If no specific VIP plan is assigned, grant full access (level 5)
        console.log('VIP with no specific plan - granting full access');
        planLevel = 5;
      }
    }
    // If not VIP, check for regular subscription
    else if (userDoc.subscription?.type) {
      const planType = userDoc.subscription.type;
      console.log('User has regular subscription:', planType);
      
      // Extract the plan level number (e.g., 'plan_2' -> 2)
      const subscriptionLevel = parseInt(planType.split('_')[1]) || 1;
      console.log('Subscription plan level:', subscriptionLevel);
      
      // Set the plan level from subscription
      planLevel = subscriptionLevel;
    } else {
      console.log('User has no subscription or VIP status');
    }
    
    console.log('Final determined plan level:', planLevel);
    return planLevel;
  }, [userDoc]);

  // Check access levels for different tools
  const planLevel = getUserPlanLevel();
  console.log('Final determined plan level:', planLevel);
  const hasInventoryToolsAccess = planLevel >= 2; // plan_2 or higher
  const hasProductToolsAccess = planLevel >= 3;  // plan_3 or higher
  console.log('Tool access:', { hasInventoryToolsAccess, hasProductToolsAccess });

  return (
    <Page
      title="Bulk Edit Products"
      subtitle={`${products.length} products loaded`}
      breadcrumbs={[{ content: 'Home', url: '/app' }]}
      primaryAction={{
        content: 'Push to Shopify',
        disabled: !hasUnsavedChanges || isSaving,
        loading: isSaving,
        onAction: () => setShowCreateNewProductsModal(true)
      }}
      secondaryActions={[
      ]}
      fullWidth
    >
      <Stack>
        <TemplateTools
          disabled={isLoading || !currentShop}
          selectedProducts={products.filter(p => selectedProductIds.has(p.id.toString()))}
          onSaveTemplate={handleSaveTemplate}
          onTemplateUpdate={handleTemplateUpdate}
          onLoadTemplate={handleLoadTemplate}
          handleProductChange={handleProductChange}
          handleOptionValueChange={handleOptionValueChange}
          metafieldChangeHandler={handleMetafieldChange}
          user={user}
          id="template-tools-ref"
        />
      </Stack>
      <Layout>
        {/* Loading States */}
        {isLoading || isSaving ? (
          <Layout.Section>
            <Card sectioned>
              <div style={{ textAlign: 'center', padding: '20px 0' }}>
                <Stack vertical spacing="tight" alignment="center">
                  <Spinner size="large" />
                  {isSaving && (
                    <>
                      <div style={{ width: '300px', marginTop: '1rem' }}>
                        <ProgressBar
                          progress={savingProgress.total > 0 ? (savingProgress.current / savingProgress.total) * 100 : 0}
                          size="small"
                        />
                      </div>
                      <p style={{ color: 'var(--p-text-subdued)', fontSize: '0.9em', marginTop: '0.5rem' }}>
                        {savingProgress.message || "Please wait while your changes are being saved"}
                      </p>
                    </>
                  )}
                  <p style={{ marginTop: '1rem', color: 'var(--p-text-subdued)' }}>
                    {isLoading ? 
                      `Loading products...` : 
                      `Saving changes (${savingProgress.current}/${savingProgress.total})...`
                    }
                  </p>
                </Stack>
              </div>
            </Card>
          </Layout.Section>
        ) : (
          <>
            {/* Price Tools Button */}
            <Layout.Section>
              <Card sectioned>
                <Stack distribution="leading" spacing="tight">
                  <Button
                    onClick={() => {
                      setPriceToolsVisible(!priceToolsVisible);
                      if (!priceToolsVisible) {
                        setInventoryToolsVisible(false);
                        setProductToolsVisible(false);
                      }
                    }}
                    disabled={selectedProductIds.size === 0}
                    primary={priceToolsVisible}
                  >
                    {priceToolsVisible ? 'Hide Price Tools' : 'Show Price Tools'}
                  </Button>
                  
                  <Tooltip content={!hasInventoryToolsAccess ? "Requires Standard plan or higher" : ""} dismissOnMouseOut>
                    <Button
                      onClick={() => {
                        setInventoryToolsVisible(!inventoryToolsVisible);
                        if (!inventoryToolsVisible) {
                          setPriceToolsVisible(false);
                          setProductToolsVisible(false);
                        }
                      }}
                      disabled={selectedProductIds.size === 0 || !hasInventoryToolsAccess}
                      primary={inventoryToolsVisible}
                    >
                      {!hasInventoryToolsAccess && <Icon source={LockMinor} color="subdued" />}
                      {inventoryToolsVisible ? 'Hide Inventory Tools' : 'Show Inventory Tools'}
                    </Button>
                  </Tooltip>
                  
                  <Tooltip content={!hasProductToolsAccess ? "Requires Premium plan or higher" : ""} dismissOnMouseOut>
                    <Button
                      onClick={() => {
                        setProductToolsVisible(!productToolsVisible);
                        if (!productToolsVisible) {
                          setPriceToolsVisible(false);
                          setInventoryToolsVisible(false);
                        }
                      }}
                      disabled={selectedProductIds.size === 0 || !hasProductToolsAccess}
                      primary={productToolsVisible}
                    >
                      {!hasProductToolsAccess && <Icon source={LockMinor} color="subdued" />}
                      {productToolsVisible ? 'Hide Product Tools' : 'Show Product Tools'}
                    </Button>
                  </Tooltip>
                </Stack>
              </Card>
            </Layout.Section>

            {/* Price Tools Section */}
            {priceToolsVisible && (
              <Layout.Section>
                <PricingTools
                  priceTools={priceTools}
                  setPriceTools={setPriceTools}
                  selectedProductIds={selectedProductIds}
                  products={products}
                  isPriceUpdating={isPriceUpdating}
                  priceChange={priceChange}
                  setPriceChange={setPriceChange}
                  priceChangeType={priceChangeType}
                  setPriceChangeType={setPriceChangeType}
                  onPriceUpdate={handlePriceUpdate}
                  editHistory={localEdits}
                  user={user}
                />
              </Layout.Section>
            )}

            {/* Inventory Tools Section */}
            {inventoryToolsVisible && (
              <Layout.Section>
                <InventoryTools
                  selectedProductIds={selectedProductIds}
                  products={products}
                  onInventoryUpdate={handleInventoryUpdate}
                  isInventoryUpdating={isInventoryUpdating}
                  editHistory={localEdits}
                  user={user}
                />
              </Layout.Section>
            )}

            {/* Product Tools Section */}
            {productToolsVisible && (
              <Layout.Section>
                <ProductTools
                  selectedProductIds={selectedProductIds}
                  products={products}
                  onProductUpdate={handleProductUpdate}
                  isProductUpdating={isProductUpdating}
                  editHistory={localEdits}
                  user={user}
                  localEdits={localEdits}
                  setLocalEdits={setLocalEdits}
                />
              </Layout.Section>
            )}

            {/* Top Bar */}
            <Layout.Section>
              <Card sectioned>
                <div style={{ marginBottom: '1rem' }}>
                  <FormLayout>
                    <FormLayout.Group>
                      <Stack alignment="center" distribution="equalSpacing">
                        <ButtonGroup segmented>
                          <Tooltip content="Grid View">
                            <Button
                              icon={ViewMajor}
                              pressed={viewMode === 'card'}
                              onClick={() => setViewMode('card')}
                            />
                          </Tooltip>
                          <Tooltip content="List View">
                            <Button
                              icon={ListMajor}
                              pressed={viewMode === 'list'}
                              onClick={() => setViewMode('list')}
                            />
                          </Tooltip>
                        </ButtonGroup>
                      </Stack>
                    </FormLayout.Group>
                  </FormLayout>
                </div>
              </Card>
            </Layout.Section>

            {/* Main Content */}
            <Layout.Section>
              <div style={{ marginTop: '0' }}>
                <Card>
                  <div style={{ padding: '16px' }}>
                    {viewMode === 'card' ? (
                      <div style={{ display: 'flex', gap: '20px', marginTop: '1rem' }}>
                        {/* Products List - Always on Left */}
                        <div style={{ width: '300px', flexShrink: 0 }}>
                          <Card 
                            title={
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                                <span>Products</span>
                                <Button
                                  onClick={handleSelectAll}
                                  size="slim"
                                >
                                  {selectedProductIds.size === products.length ? 'Deselect All' : 'Select All'}
                                </Button>
                              </div>
                            }
                          >
                            <div style={{ maxHeight: 'calc(100vh - 300px)', overflowY: 'auto' }}>
                              {products.map((product) => (
                                <div
                                  key={product.id}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    padding: '12px',
                                    cursor: 'pointer',
                                    backgroundColor: selectedProductIds.has(product.id.toString()) ? 'var(--p-surface-selected)' : 'transparent',
                                    borderRadius: '4px',
                                  }}
                                >
                                  <div onClick={(e) => e.stopPropagation()}>
                                    <Checkbox
                                      checked={selectedProductIds.has(product.id.toString())}
                                      onChange={() => handleProductSelect(product.id.toString())}
                                      label=""
                                    />
                                  </div>
                                  {product.featuredImage && (
                                    <img
                                      src={product.featuredImage.url || '/images/no-image.png'} 
                                      alt={(localEdits[product.id]?.image_alt_text ?? (product.featuredImage?.altText || (product.images && product.images[0]?.altText))) || product.title}
                                      style={{
                                        width: '40px',
                                        height: '40px',
                                        objectFit: 'cover',
                                        marginRight: '12px',
                                        borderRadius: '4px',
                                        border: '1px solid var(--p-border-subdued)'
                                      }}
                                    />
                                  )}
                                  <div 
                                    onClick={() => {
                                      setExpandedProducts(new Set([product.id.toString()]));
                                    }}
                                    style={{ display: 'flex', flex: 1, alignItems: 'center', marginLeft: '8px' }}
                                  >
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                      <Text variant="bodyMd" as="p" fontWeight="medium">
                                        {product.title}
                                      </Text>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </Card>
                        </div>

                        {/* Product Details - Right Side */}
                        <div style={{ flex: 1 }}>
                          {products.map(product => {
                            if (!expandedProducts.has(product.id.toString())) return null;
                            return (
                              <div key={product.id}>
                                {renderProductCard(product)}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ) : (
                      <div style={{ position: 'relative' }}>
                        <div style={{ marginBottom: '20px' }}>
                          <ButtonGroup>
                            <Button
                              onClick={handleSelectAll}
                              primary={selectedProductIds.size === products.length}
                            >
                              {selectedProductIds.size === products.length ? 'Deselect All' : 'Select All'}
                            </Button>
                            <div>
                              Selected: {selectedProductIds.size} of {products.length}
                            </div>
                          </ButtonGroup>
                          <div style={{ marginTop: '10px', marginBottom: '10px' }}>
                          <ButtonGroup>
                            <Button
                              onClick={() => {
                                // Check if all columns (except first two) are visible
                                const allVisible = columnVisibility.slice(2).every(v => v === true);
                                toggleAllColumnsVisibility(!allVisible);
                              }}
                            >
                              {columnVisibility.slice(2).every(v => v === true) 
                                ? 'Deselect All Columns' 
                                : 'Select All Columns'}
                            </Button>
                          </ButtonGroup>
                        </div>
                          <ButtonGroup segmented>
                            {columnNames.map((name, index) => (
                              <Button
                                key={name}
                                pressed={columnVisibility[index]}
                                onClick={() => toggleColumnVisibility(index)}
                              >
                                {name}
                              </Button>
                            ))}
                          </ButtonGroup>
                        </div>
                        <div className="quill-container" style={{ position: 'relative', zIndex: 1 }}>
                          <DataTable
                            columnContentTypes={columnNames.map(header => 
                              header === 'Variant Price' || header === 'Variant Compare at Price' ? 'numeric' : 'text'
                            ).filter((_, index) => columnVisibility[index])}
                            headings={columnNames.filter((_, index) => columnVisibility[index]).map(header => (
                              <div 
                                style={{
                                  position: 'relative',
                                  cursor: 'col-resize',
                                  userSelect: 'none',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  width: columnWidths[header] || 'auto',
                                  padding: '8px'
                                }}
                              >
                                {header}
                                <div 
                                  style={{
                                    position: 'absolute',
                                    right: 0,
                                    top: 0,
                                    bottom: 0,
                                    width: '10px',
                                    cursor: 'col-resize'
                                  }}
                                  onMouseDown={(e) => handleResizeStart(e, header)}
                                />
                              </div>
                            ))}
                            rows={products.map(product => {
                              const isSelected = selectedProductIds.has(product.id.toString());
                              const productEdits = localEdits[product.id] || {};
                              const firstVariant = product.variants?.[0];
                              
                              const rowData = [
                                <div style={{ width: columnWidths['Checkbox'] }}>
                                  <Checkbox
                                    checked={isSelected}
                                    onChange={(checked) => handleProductSelect(product.id.toString(), checked)}
                                  />
                                </div>,
                                <div style={{ width: columnWidths['Image'] }}>
                                  <img
                                    src={product.featuredImage?.url || (product.images && product.images.length > 0 ? product.images[0].url : '/images/no-image.png')} 
                                    alt={(localEdits[product.id]?.image_alt_text ?? (product.featuredImage?.altText || (product.images && product.images[0]?.altText))) || product.title}
                                    style={{
                                      width: '40px',
                                      height: '40px',
                                      objectFit: 'cover',
                                      borderRadius: '4px',
                                      border: '1px solid var(--p-border-subdued)'
                                    }}
                                  />
                                </div>,
                                <div style={{ width: columnWidths['Title'] }}>
                                  <Stack vertical>
                                    <TextField
                                      value={localEdits[product.id]?.title ?? product.title}
                                      {...createProductFieldChangeHandler(product.id, 'title')}
                                      autoComplete="off"
                                      type="text"
                                      id={`title-list-${product.id}`}
                                    />
                                  </Stack>
                                </div>,
                                <div style={{ width: columnWidths['Handle'] }}>
                                  <Stack vertical>
                                    <TextField
                                      value={localEdits[product.id]?.handle ?? product.handle}
                                      {...createProductFieldChangeHandler(product.id, 'handle')}
                                      autoComplete="off"
                                      type="text"
                                      id={`handle-list-${product.id}`}
                                    />
                                  </Stack>
                                </div>,
                                <div style={{ 
                                  width: columnWidths['Description'],
                                  position: 'relative',
                                  maxWidth: '100%'
                                }}>
                                  <div style={{ width: '100%', maxWidth: '100%' }}>
                                    <SummernoteEditor
                                      value={productEdits.descriptionHtml ?? product.descriptionHtml}
                                      onChange={(content) => handleProductChange(product.id, 'descriptionHtml', content)}
                                      height={300}
                                    />
                                  </div>
                                </div>,
                                <div style={{ width: columnWidths['Product Type'] }}>
                                  <Stack vertical>
                                    <TextField
                                      value={productEdits.product_type ?? product.productType}
                                      onChange={(value) => handleProductChange(product.id, 'product_type', value)}
                                      clearButton
                                      onClearButtonClick={(e) => {
                                        if (e && e.stopPropagation) e.stopPropagation();
                                        handleProductChange(product.id, 'product_type', '');
                                      }}
                                      autoComplete="off"
                                      type="text"
                                      readOnly={false}
                                      id={`product-type-${product.id}`}
                                      focused={false}
                                      onFocus={(e) => { if (e && e.stopPropagation) e.stopPropagation(); }}
                                      onClick={(e) => { if (e && e.stopPropagation) e.stopPropagation(); }}
                                    />
                                  </Stack>
                                </div>,
                                <div style={{ width: columnWidths['Tags'] }}>
                                  <Stack vertical>
                                    <TextField
                                      value={productEdits.tags ?? (Array.isArray(product.tags) ? product.tags.join(', ') : product.tags || '')}
                                      onChange={(value) => handleProductChange(product.id, 'tags', value)}
                                      clearButton
                                      onClearButtonClick={(e) => {
                                        if (e && e.stopPropagation) e.stopPropagation();
                                        handleProductChange(product.id, 'tags', '');
                                      }}
                                      autoComplete="off"
                                      type="text"
                                      readOnly={false}
                                      id={`tags-${product.id}`}
                                      focused={false}
                                      onFocus={(e) => { if (e && e.stopPropagation) e.stopPropagation(); }}
                                      onClick={(e) => { if (e && e.stopPropagation) e.stopPropagation(); }}
                                    />
                                  </Stack>
                                </div>,
                                <div style={{ width: columnWidths['Vendor'] }}>
                                  <Stack vertical>
                                    <TextField
                                      value={(localEdits[product.id]?.vendor ?? product.vendor) || ''}
                                      {...createProductFieldChangeHandler(product.id, 'vendor')}
                                      autoComplete="off"
                                      type="text"
                                      id={`vendor-list-${product.id}`}
                                    />
                                  </Stack>
                                </div>,
                                <div style={{ width: columnWidths['Product Image Alt Text'] }}>
                                  <Stack vertical>
                                    <TextField
                                      value={(localEdits[product.id]?.image_alt_text ?? (product.featuredImage?.altText || (product.images && product.images[0]?.altText))) || ''}
                                      {...createProductFieldChangeHandler(product.id, 'image_alt_text')}
                                      autoComplete="off"
                                      type="text"
                                      id={`featured-image-alt-text-list-${product.id}`}
                                      helpText="This alt text will be used for all product images"
                                    />
                                  </Stack>
                                </div>,
                                <div style={{ width: columnWidths['Option1 Name'] }}>
                                  <Stack vertical>
                                    <TextField
                                      label="Option 1 Name"
                                      value={(localEdits[product.id]?.option1_name ?? product.options?.[0]?.name) || ''}
                                      onChange={(value) => {
                                        setLocalEdits(prev => ({
                                          ...prev,
                                          [product.id]: {
                                            ...(prev[product.id] || {}),
                                            option1_name: value
                                          }
                                        }));
                                      }}
                                      onClearButtonClick={(e) => {
                                        if (e && e.stopPropagation) e.stopPropagation();
                                        setLocalEdits(prev => ({
                                          ...prev,
                                          [product.id]: {
                                            ...(prev[product.id] || {}),
                                            option1_name: ''
                                          }
                                        }));
                                      }}
                                      onFocus={(e) => { if (e && e.stopPropagation) e.stopPropagation(); }}
                                      onClick={(e) => { if (e && e.stopPropagation) e.stopPropagation(); }}
                                      autoComplete="off"
                                      type="text"
                                      id={`option1-name-list-${product.id}`}
                                    />
                                  </Stack>
                                </div>,
                                <div style={{ width: columnWidths['Option1 Values'] }}>
                                  <Stack vertical spacing="tight">
                                    {product.options && product.options[0] && product.options[0].values ? (
                                      product.options[0].values.map((value, valueIndex) => (
                                        <TextField
                                          key={`${product.id}-option1-value-${valueIndex}`}
                                          value={value || ''}
                                          onChange={(newValue) => {
                                            setLocalEdits(prev => ({
                                              ...prev,
                                              [product.id]: {
                                                ...(prev[product.id] || {}),
                                                option1_values: {
                                                  ...(prev[product.id]?.option1_values || {}),
                                                  [valueIndex]: newValue
                                                }
                                              }
                                            }));
                                          }}
                                          autoComplete="off"
                                          type="text"
                                          id={`option1-value-${valueIndex}-${product.id}`}
                                          error={isVariantFieldChanged(product.id, product.options[0].id, `option1_values.${valueIndex}`, value)}
                                        />
                                      ))
                                    ) : (
                                      <div>No Option 1 Values</div>
                                    )}
                                  </Stack>
                                </div>,
                                <div style={{ width: columnWidths['Option2 Name'] }}>
                                  {product.options && product.options.length > 1 && (
                                    <Stack vertical>
                                      <TextField
                                        label="Option 2 Name"
                                        value={(localEdits[product.id]?.option2_name ?? product.options?.[1]?.name) || ''}
                                        onChange={(value) => {
                                          setLocalEdits(prev => ({
                                            ...prev,
                                            [product.id]: {
                                              ...(prev[product.id] || {}),
                                              option2_name: value
                                            }
                                          }));
                                        }}
                                        onClearButtonClick={(e) => {
                                          if (e && e.stopPropagation) e.stopPropagation();
                                          setLocalEdits(prev => ({
                                            ...prev,
                                            [product.id]: {
                                              ...(prev[product.id] || {}),
                                              option2_name: ''
                                            }
                                          }));
                                        }}
                                        onFocus={(e) => { if (e && e.stopPropagation) e.stopPropagation(); }}
                                        onClick={(e) => { if (e && e.stopPropagation) e.stopPropagation(); }}
                                        autoComplete="off"
                                        type="text"
                                        id={`option2-name-list-${product.id}`}
                                      />
                                    </Stack>
                                  )}
                                </div>,
                                <div style={{ width: columnWidths['Option2 Values'] }}>
                                  {product.options && product.options.length > 1 && (
                                    <Stack vertical spacing="tight">
                                      {(product.options?.[1]?.values || []).map((value, valueIndex) => {
                                        const optionValue = localEdits[product.id]?.option2_values?.[valueIndex] ?? value;
                                          
                                        return (
                                          <TextField
                                            key={`option2-value-${valueIndex}-${product.id}`}
                                            value={optionValue}
                                            onChange={(newValue) => {
                                              setLocalEdits(prev => ({
                                                ...prev,
                                                [product.id]: {
                                                  ...(prev[product.id] || {}),
                                                  option2_values: {
                                                    ...(prev[product.id]?.option2_values || {}),
                                                    [valueIndex]: newValue
                                                  }
                                                }
                                              }));
                                            }}
                                            autoComplete="off"
                                            type="text"
                                            id={`option2-value-${valueIndex}-list-${product.id}`}
                                            error={isVariantFieldChanged(product.id, product.options[1].id, `option2_values.${valueIndex}`, value)}
                                          />
                                        );
                                      })}
                                    </Stack>
                                  )}
                                </div>,
                                <div style={{ width: columnWidths['Option3 Name'] }}>
                                  {product.options && product.options.length > 2 && (
                                    <Stack vertical>
                                      <TextField
                                        label="Option 3 Name"
                                        value={(localEdits[product.id]?.option3_name ?? product.options?.[2]?.name) || ''}
                                        onChange={(value) => {
                                          setLocalEdits(prev => ({
                                            ...prev,
                                            [product.id]: {
                                              ...(prev[product.id] || {}),
                                              option3_name: value
                                            }
                                          }));
                                        }}
                                        onClearButtonClick={(e) => {
                                          if (e && e.stopPropagation) e.stopPropagation();
                                          setLocalEdits(prev => ({
                                            ...prev,
                                            [product.id]: {
                                              ...(prev[product.id] || {}),
                                              option3_name: ''
                                            }
                                          }));
                                        }}
                                        onFocus={(e) => { if (e && e.stopPropagation) e.stopPropagation(); }}
                                        onClick={(e) => { if (e && e.stopPropagation) e.stopPropagation(); }}
                                        autoComplete="off"
                                        type="text"
                                        id={`option3-name-list-${product.id}`}
                                      />
                                    </Stack>
                                  )}
                                </div>,
                                <div style={{ width: columnWidths['Option3 Values'] }}>
                                  {product.options && product.options.length > 2 && (
                                    <Stack vertical spacing="tight">
                                      {(product.options?.[2]?.values || []).map((value, valueIndex) => {
                                        const optionValue = localEdits[product.id]?.option3_values?.[valueIndex] ?? value;
                                          
                                        return (
                                          <TextField
                                            key={`option3-value-${valueIndex}-${product.id}`}
                                            value={optionValue}
                                            onChange={(newValue) => {
                                              setLocalEdits(prev => ({
                                                ...prev,
                                                [product.id]: {
                                                  ...(prev[product.id] || {}),
                                                  option3_values: {
                                                    ...(prev[product.id]?.option3_values || {}),
                                                    [valueIndex]: newValue
                                                  }
                                                }
                                              }));
                                            }}
                                            autoComplete="off"
                                            type="text"
                                            id={`option3-value-${valueIndex}-list-${product.id}`}
                                            error={isVariantFieldChanged(product.id, product.options[2].id, `option3_values.${valueIndex}`, value)}
                                          />
                                        );
                                      })}
                                    </Stack>
                                  )}
                                </div>,
                                <div style={{ width: columnWidths['Variant Options'] }}>
                                  <Stack vertical spacing="tight">
                                    {product.variants.map((variant, index) => {
                                      const variantTitle = getVariantTitle(product, variant);
                                      
                                      return (
                                        <TextField
                                          key={`${variant.id}-options`}
                                          value={variantTitle || `Variant ${index + 1}`}
                                          readOnly
                                          autoComplete="off"
                                          type="text"
                                          id={`options-${variant.id}`}
                                        />
                                      );
                                    })}
                                  </Stack>
                                </div>,
                                <div style={{ width: columnWidths['Variant SKU'] }}>
                                  <Stack vertical spacing="tight">
                                    {product.variants.map((variant, index) => (
                                      <TextField
                                        key={`${variant.id}-sku`}
                                        value={getVariantFieldValue(product.id, variant.id, 'sku', variant.sku, localEdits)}
                                        {...createVariantFieldChangeHandler(product.id, variant.id, 'sku')}
                                        autoComplete="off"
                                        type="text"
                                        id={`sku-${variant.id}`}
                                      />
                                    ))}
                                  </Stack>
                                </div>,
                                <div style={{ width: columnWidths['Variant Price'] }}>
                                  <Stack vertical spacing="tight">
                                    {product.variants.map((variant, index) => (
                                      <TextField
                                        key={`${variant.id}-price`}
                                        value={formatPriceForDisplay(getVariantFieldValue(product.id, variant.id, 'price', variant.price, localEdits))}
                                        {...createVariantFieldChangeHandler(product.id, variant.id, 'price', formatPriceForApi)}
                                        prefix="$"
                                        autoComplete="off"
                                        type="text"
                                        id={`price-${variant.id}`}
                                      />
                                    ))}
                                  </Stack>
                                </div>,
                                <div style={{ width: columnWidths['Variant Compare at Price'] }}>
                                  <Stack vertical spacing="tight">
                                    {product.variants.map((variant, index) => (
                                      <TextField
                                        key={`${variant.id}-compare-price`}
                                        value={formatPriceForDisplay(getVariantFieldValue(product.id, variant.id, 'compare_at_price', variant.compareAtPrice, localEdits))}
                                        {...createVariantFieldChangeHandler(product.id, variant.id, 'compare_at_price', formatPriceForApi)}
                                        prefix="$"
                                        autoComplete="off"
                                        type="text"
                                        id={`compare-at-price-${variant.id}`}
                                      />
                                    ))}
                                  </Stack>
                                </div>,
                                <div style={{ width: columnWidths['Variant Inventory'] }}>
                                  <Stack vertical spacing="tight">
                                    {product.variants.map((variant, index) => (
                                      <TextField
                                        key={`${variant.id}-inventory`}
                                        value={getVariantFieldValue(product.id, variant.id, 'inventory_quantity', variant.inventoryQuantity, localEdits)}
                                        {...createVariantFieldChangeHandler(product.id, variant.id, 'inventory_quantity')}
                                        autoComplete="off"
                                        type="text"
                                        id={`inventory-${variant.id}`}
                                      />
                                    ))}
                                  </Stack>
                                </div>,
                                <div style={{ width: columnWidths['Metafields'] }}>
                                  {product.metafields && product.metafields.length > 0 && (
                                    <Stack vertical spacing="tight">
                                      {product.metafields.map((metafield, index) => {
                                        const metafieldKey = `${metafield.namespace}.${metafield.key}`;
                                        const metafieldValue = localEdits[product.id]?.metafield_updates?.[metafieldKey] || 
                                                              localEdits[product.id]?.metafields?.[metafieldKey]?.value || 
                                                              metafield.value;
                                        
                                        return (
                                          <TextField
                                            key={metafield.id || `new-${index}`}
                                            label={`${metafield.namespace}.${metafield.key}`}
                                            value={metafieldValue}
                                            {...createMetafieldChangeHandler(product.id, metafield.namespace, metafield.key)}
                                            autoComplete="off"
                                            type="text"
                                            id={`metafield-list-${metafield.namespace}-${metafield.key}-${product.id}`}
                                            clearButton
                                            onFocus={(e) => { if (e && e.stopPropagation) e.stopPropagation(); }}
                                            onClick={(e) => { if (e && e.stopPropagation) e.stopPropagation(); }}
                                          />
                                        );
                                      })}
                                    </Stack>
                                  )}
                                </div>,
                              ];
                              
                              return rowData.filter((_, index) => columnVisibility[index]);
                            })}
                            hasZebraStriping
                            increasedTableDensity
                            columnWidths={columnWidths}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </Card>
              </div>
            </Layout.Section>
          </>
        )}
      </Layout>
      {/* Create New Products Modal */}
      <Modal
        open={showCreateNewProductsModal}
        onClose={() => setShowCreateNewProductsModal(false)}
        title="Create New Products"
        primaryAction={{
          content: 'Create New Products',
          onAction: () => {
            setShowCreateNewProductsModal(false);
            handleCreateNewProducts(
              currentShop,
              user,
              localEdits,
              products,
              setIsSaving,
              setSavingProgress,
              setLocalEdits,
              setHasUnsavedChanges,
              showToast,
              setError,
              handleFetchProducts,
              { 
                deleteOldProducts,
                showConfirmModal: true,
                setShowConfirmModal: setShowCreationConfirmModal
              }
            );
          }
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => setShowCreateNewProductsModal(false)
          }
        ]}
      >
        <Modal.Section>
          <TextContainer>
            <p>
              Instead of updating the existing products, this will create <strong>new products</strong> with your changes.
              The original products will be archived by default to maintain your store's history.
            </p>
            <p>
              This will create {Object.keys(localEdits).length} new products in your Shopify store, including all product data:
            </p>
            <ul>
              <li>Basic product details (title, description, vendor, type, etc.)</li>
              <li>Variants and their attributes (price, SKU, inventory, etc.)</li>
              <li>Images and media</li>
              <li>Metafields and custom data</li>
              <li>SEO information</li>
            </ul>
            <p>
              All your current edits will be applied to the new products.
            </p>
            <Checkbox
              label="Delete original products instead of archiving them (not recommended)"
              checked={deleteOldProducts}
              onChange={setDeleteOldProducts}
              helpText="Archiving is generally preferred as it maintains your store's history and allows for recovery if needed."
            />
          </TextContainer>
        </Modal.Section>
      </Modal>
      {/* Creation Confirm Modal */}
      <Modal
        open={showCreationConfirmModal}
        onClose={() => setShowCreationConfirmModal(false)}
        title="Product Creation Complete"
        primaryAction={{
          content: 'Refresh Products',
          onAction: () => {
            setShowCreationConfirmModal(false);
            // Get product IDs from sessionStorage
            const results = JSON.parse(sessionStorage.getItem('productCreationResults') || '{}');
            if (results.productIds && results.productIds.length > 0) {
              // Refresh products with the new product IDs
              handleFetchProducts(
                currentShop,
                results.productIds,
                setIsLoading,
                setError,
                setProducts,
                setLoadingProgress
              );
            }
          }
        }}
        secondaryActions={[
          {
            content: 'Close',
            onAction: () => setShowCreationConfirmModal(false)
          }
        ]}
      >
        <Modal.Section>
          {(() => {
            const results = JSON.parse(sessionStorage.getItem('productCreationResults') || '{}');
            return (
              <div>
                <TextContainer>
                  <p>
                    Successfully created {results.success || 0} new products. 
                    {results.deleteOldProducts ? ' Original products were deleted.' : ' Original products were archived.'}
                  </p>
                  <p>
                    You can view the logs for more details about the creation process.
                  </p>
                  <p>
                    Would you like to refresh the product list now to see the newly created products?
                  </p>
                </TextContainer>
              </div>
            );
          })()} 
        </Modal.Section>
      </Modal>
      {/* Toast notification */}
      {toastContext && toastContext.toast && toastContext.toast.active && (
        <Frame>
          <Toast
            content={toastContext.toast.content}
            error={toastContext.toast.error}
            onDismiss={toastContext.dismissToast}
          />
        </Frame>
      )}
    </Page>
  );
}
